import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate, formatTransactionDate } from '../utils/formatters';
import { Search, Calendar, Edit2, Trash2, ChevronDown, ChevronUp, X, SlidersHorizontal, ArrowRightLeft, CheckSquare, Check, Loader2 } from 'lucide-react';
import { useTransaction } from '../contexts/TransactionContext';
import { usePlans } from '../contexts/PlanContext';
import { useAccounts } from '../contexts/AccountContext';
import { useCategories } from '../contexts/CategoryContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { Transaction } from '../types/transaction';
import { Notification, NotificationType } from '../components/Notification';
import Header from '../components/Header';
import { PixelHourglass } from '../components/PixelHourglass';

// PERFORMANCE: Debounce hook for search input
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function HistoryPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { transactions, fetchTransactions, deleteTransaction, loading, bulkDeleteTransactions, bulkChangeCategory, bulkChangeAccount } = useTransaction();
  const { allMonthPlans } = usePlans();
  const { refreshAccounts, accounts } = useAccounts();
  const { refreshCategories, categories } = useCategories();
  const { formatMoney } = useUserPreferences();

  // Helper to translate category name (for system categories like Balance Adjustment)
  const translateCategoryName = useCallback((categoryName: string | null | undefined): string | null => {
    if (!categoryName) return null;
    // Translate "Penyesuaian Saldo" to "Balance Adjustment" for English users
    if (categoryName === 'Penyesuaian Saldo' && i18n.language === 'en') {
      return t('balance_adjustment.category_name');
    }
    return categoryName;
  }, [i18n.language, t]);

  // Helper to translate default transaction name and balance adjustment descriptions
  const translateTransactionName = useCallback((name: string | null | undefined, categoryName?: string | null): string => {
    // Translate default "Transaksi" to "Transaction" for English users
    if ((!name || name === 'Transaksi') && i18n.language === 'en') {
      return t('history.default_transaction_name');
    }
    
    // Translate balance adjustment descriptions for English users
    if (name && i18n.language === 'en' && (categoryName === 'Penyesuaian Saldo' || categoryName === 'Balance Adjustment')) {
      // Check for "Penyesuaian Saldo [account name]" pattern
      if (name.startsWith('Penyesuaian Saldo ')) {
        const accountName = name.replace('Penyesuaian Saldo ', '');
        return t('balance_adjustment.balance_adjustment', { name: accountName });
      }
      // Check for "Penutupan Rekening [account name]" pattern
      if (name.startsWith('Penutupan Rekening ')) {
        const accountName = name.replace('Penutupan Rekening ', '');
        return t('balance_adjustment.account_closing', { name: accountName });
      }
    }
    
    return name || t('history.default_transaction_name');
  }, [i18n.language, t]);

  // Helper to format month name with translation
  const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const formatMonthName = useCallback((monthDate: Date): string => {
    return `${t(`budget.months.${monthKeys[monthDate.getMonth()]}`)} ${monthDate.getFullYear()}`;
  }, [t]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set());

  // Filter states
  const [filterType, setFilterType] = useState<'all' | 'income' | 'expense' | 'transfer'>('all'); // Transaction type filter (local)
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedPlanMonthId, setSelectedPlanMonthId] = useState<string>(''); // Plan month filter
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(''); // Can be parent or child category ID
  const [selectedCategoryType, setSelectedCategoryType] = useState<'parent' | 'child' | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false); // Collapsible filter section

  // Bulk selection states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState<string>('');
  const [bulkAccountId, setBulkAccountId] = useState<string>('');

  // Notification state
  const [notification, setNotification] = useState<{ type: NotificationType; message: string } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const isLoadingMoreRef = useRef<boolean>(false);
  const PAGE_SIZE = 10;

  // PERFORMANCE: Debounce search query to reduce filtering operations
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // PERFORMANCE: Memoize toggle function to prevent unnecessary re-renders
  const toggleExpanded = useCallback((id: string) => {
    setExpandedTransactions(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      return newExpanded;
    });
  }, []);

  // Handle pre-applied filters from navigation (e.g., from budget page or account page)
  useEffect(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(location.search);
    const accountIdParam = urlParams.get('accountId');
    const dateFromParam = urlParams.get('dateFrom');
    const dateToParam = urlParams.get('dateTo');

    if (accountIdParam) {
      setSelectedAccountId(accountIdParam);
      setShowFilters(true);
    }
    if (dateFromParam) setDateFrom(dateFromParam);
    if (dateToParam) setDateTo(dateToParam);

    // Also check navigation state
    const state = location.state as {
      categoryId?: string;
      parentCategoryId?: string;
      accountId?: string;
      monthPlanId?: string;
      dateFrom?: string;
      dateTo?: string;
    } | null;

    if (state) {
      // Apply filters from navigation state
      if (state.dateFrom) setDateFrom(state.dateFrom);
      if (state.dateTo) setDateTo(state.dateTo);
      if (state.accountId) setSelectedAccountId(state.accountId);
      if (state.monthPlanId) setSelectedPlanMonthId(state.monthPlanId);

      if (state.categoryId) {
        setSelectedCategoryId(state.categoryId);
        setSelectedCategoryType('child');
        // Don't auto-expand filters - keep collapsed like RekeningPage
      } else if (state.parentCategoryId) {
        setSelectedCategoryId(state.parentCategoryId);
        setSelectedCategoryType('parent');
        // Don't auto-expand filters - keep collapsed like RekeningPage
      }
    }
  }, [location.state, location.search]);

  // Initialize date range to 31 days from today (not tied to budget page)
  useEffect(() => {
    // Only set default dates if not coming from navigation state AND no account filter
    // (account filter without dates = show all transactions for that account)
    if (!location.state && !dateFrom && !dateTo && !selectedAccountId) {
      const today = new Date();
      const thirtyOneDaysAgo = new Date(today);
      thirtyOneDaysAgo.setDate(today.getDate() - 31);

      setDateFrom(thirtyOneDaysAgo.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [location.state, dateFrom, dateTo, selectedAccountId]);

  // Fetch transactions with filters (initial load and filter changes)
  useEffect(() => {
    const hasDateFilter = dateFrom && dateTo;
    const hasAccountOrCategoryFilter = selectedAccountId || selectedCategoryId;

    if (hasDateFilter || hasAccountOrCategoryFilter) {
      // Reset pagination and load first page ONLY when filters change
      console.log('🔄 Filter changed - Resetting to page 0');
      setCurrentPage(0);
      setHasMore(true);

      fetchTransactions(
        selectedPlanMonthId || null,
        selectedCategoryType === 'child' ? selectedCategoryId : undefined,
        selectedCategoryType === 'parent' ? selectedCategoryId : undefined,
        selectedAccountId || undefined,
        dateFrom || undefined,
        dateTo || undefined,
        PAGE_SIZE,
        0,
        false
      ).then(result => {
        console.log(`✅ Initial load: ${result.transactions.length} transactions, hasMore: ${result.hasMore}`);
        setHasMore(result.hasMore);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPlanMonthId,
    selectedCategoryId,
    selectedCategoryType,
    selectedAccountId,
    dateFrom,
    dateTo
    // NOTE: Intentionally excluding fetchTransactions and PAGE_SIZE to prevent unnecessary re-fetches
  ]);

  // PERFORMANCE: Memoize category options to avoid rebuilding on every render
  const categoryOptions = useMemo(() => {
    const options: Array<{ value: string; label: string; type: 'parent' | 'child'; isParent?: boolean }> = [];

    categories.forEach((group) => {
      // Add parent category
      options.push({
        value: `parent:${group.id}`,
        label: group.name,
        type: 'parent',
        isParent: true
      });

      // Add subcategories
      group.children.forEach((child) => {
        options.push({
          value: `child:${child.id}`,
          label: `${group.name} - ${child.name}`,
          type: 'child'
        });
      });
    });

    return options;
  }, [categories]);

  // PERFORMANCE: Memoize handlers
  const handleCategoryChange = useCallback((value: string) => {
    if (!value) {
      setSelectedCategoryId('');
      setSelectedCategoryType(null);
      return;
    }

    const [type, id] = value.split(':');
    setSelectedCategoryId(id);
    setSelectedCategoryType(type as 'parent' | 'child');
  }, []);

  // PERFORMANCE: Memoize clear filters handler
  const clearFilters = useCallback(() => {
    setFilterType('all');
    setSelectedAccountId('');
    setSelectedPlanMonthId('');
    setSelectedCategoryId('');
    setSelectedCategoryType(null);

    // Reset to 31 days from today (not tied to budget page)
    const today = new Date();
    const thirtyOneDaysAgo = new Date(today);
    thirtyOneDaysAgo.setDate(today.getDate() - 31);
    setDateFrom(thirtyOneDaysAgo.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);

    // Reset pagination
    setCurrentPage(0);
    setHasMore(true);
  }, []);

  // Bulk selection handlers
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => !prev);
    setSelectedTransactions(new Set());
  }, []);

  const toggleTransactionSelection = useCallback((transactionId: string, isSystemTransaction: boolean) => {
    // Don't allow selecting system transactions
    if (isSystemTransaction) return;

    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  }, []);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (filterType !== 'all') count++;
    if (selectedAccountId) count++;
    if (selectedPlanMonthId) count++;
    if (selectedCategoryId) count++;
    // Check if dates are different from default (31 days from today)
    const today = new Date();
    const thirtyOneDaysAgo = new Date(today);
    thirtyOneDaysAgo.setDate(today.getDate() - 31);
    const defaultFrom = thirtyOneDaysAgo.toISOString().split('T')[0];
    const defaultTo = today.toISOString().split('T')[0];
    if (dateFrom !== defaultFrom || dateTo !== defaultTo) count++;
    return count;
  };

  // PERFORMANCE: Memoize transaction type helper to prevent recreation
  const getTransactionType = useCallback((transaction: Transaction): 'income' | 'expense' | 'transfer' => {
    // Transfer transactions have transfer_pair_id
    if (transaction.transfer_pair_id) {
      return 'transfer';
    }
    // Use transaction_type from database (income or expense)
    return transaction.transaction_type;
  }, []);

  // Note: Removed totalIncome/totalExpense calculation as it's not used in the UI

  // PERFORMANCE: Memoize search matcher - use debouncedSearchQuery
  const matchesSearch = useCallback((transaction: Transaction, query: string): boolean => {
    if (!query) return true;

    const lowerQuery = query.toLowerCase();
    const description = (transaction.name || '').toLowerCase();
    const categoryName = (transaction.category_name || '').toLowerCase();
    const parentCategoryName = (transaction.parent_category_name || '').toLowerCase();
    const accountName = (transaction.account_name || '').toLowerCase();
    const fromAccountName = (transaction.from_account_name || '').toLowerCase();
    const toAccountName = (transaction.to_account_name || '').toLowerCase();

    // Check main transaction
    const mainMatches = description.includes(lowerQuery) ||
      categoryName.includes(lowerQuery) ||
      parentCategoryName.includes(lowerQuery) ||
      accountName.includes(lowerQuery) ||
      fromAccountName.includes(lowerQuery) ||
      toAccountName.includes(lowerQuery);

    if (mainMatches) return true;

    // Check children if has children
    if (transaction.have_child && transaction.children) {
      return transaction.children.some(child => {
        const childDesc = (child.category_name || '').toLowerCase();
        const childParent = (child.parent_category_name || '').toLowerCase();
        return childDesc.includes(lowerQuery) || childParent.includes(lowerQuery);
      });
    }

    return false;
  }, []);

  // PERFORMANCE: Memoize filtered transactions - backend already filters by account/category
  // Client-side only needs to filter by type (for transfer display logic) and search
  const filteredTransactions = useMemo(() => {
    return transactions
      .filter((t) => {
        const isTransfer = !!t.transfer_pair_id;
        const transactionType = getTransactionType(t);

        // Handle transfer display logic to avoid showing duplicates
        if (isTransfer) {
          // If transfer filter selected, show expense side only (FROM account)
          if (filterType === 'transfer') {
            if (t.transaction_type === 'income') return false;
          }
          // If income filter selected
          else if (filterType === 'income') {
            if (t.transaction_type === 'expense') return false;
            if (selectedAccountId && t.account_id !== selectedAccountId) return false;
          }
          // If expense filter selected
          else if (filterType === 'expense') {
            if (t.transaction_type === 'income') return false;
            if (selectedAccountId && t.account_id !== selectedAccountId) return false;
          }
          // If 'all' filter
          else if (filterType === 'all') {
            if (selectedAccountId) {
              if (t.account_id !== selectedAccountId) return false;
            } else {
              // No account filter: show expense side only to avoid duplicates
              if (t.transaction_type === 'income') return false;
            }
          }
        }

        // Apply type filter for non-transfers
        if (!isTransfer && filterType !== 'all' && filterType !== 'transfer') {
          if (transactionType !== filterType) {
            return false;
          }
        }

        // If transfer filter selected, only show transfers
        if (filterType === 'transfer' && !isTransfer) {
          return false;
        }

        // Apply search filter with debounced query
        return matchesSearch(t, debouncedSearchQuery);
      })
      .sort((a, b) => {
        // Primary sort: by date (newest first)
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        if (dateB !== dateA) {
          return dateB - dateA;
        }
        // Secondary sort: by created_at (newest first) when dates are the same
        const createdA = new Date(a.created_at).getTime();
        const createdB = new Date(b.created_at).getTime();
        return createdB - createdA;
      });
  }, [transactions, filterType, selectedAccountId, debouncedSearchQuery, getTransactionType, matchesSearch]);

  // Note: Removed selectAllTransactions and clearSelection as they're not used in current UI

  // PERFORMANCE: Optimize auto-expand - only run when debounced search or category changes
  useEffect(() => {
    // Only auto-expand if there's an active search or category filter
    if (!debouncedSearchQuery && !selectedCategoryId) {
      setExpandedTransactions(new Set());
      return;
    }

    const transactionsToExpand = new Set<string>();
    const lowerQuery = debouncedSearchQuery.toLowerCase();

    // Use transactions directly, not filteredTransactions to avoid dependency loop
    for (const transaction of transactions) {
      if (!transaction.have_child || !transaction.children) continue;

      // Check if parent itself matches search
      let parentMatchesSearch = true;
      if (debouncedSearchQuery) {
        parentMatchesSearch = (transaction.name || '').toLowerCase().includes(lowerQuery) ||
          (transaction.category_name || '').toLowerCase().includes(lowerQuery) ||
          (transaction.parent_category_name || '').toLowerCase().includes(lowerQuery);
      }

      // Check if parent itself matches category filter
      let parentMatchesCategory = true;
      if (selectedCategoryId) {
        if (selectedCategoryType === 'child') {
          parentMatchesCategory = transaction.category_id === selectedCategoryId;
        } else if (selectedCategoryType === 'parent') {
          parentMatchesCategory = transaction.parent_category_id === selectedCategoryId;
        }
      }

      // If parent doesn't match but children might, check children
      if (!parentMatchesSearch || !parentMatchesCategory) {
        let shouldExpand = false;

        // Check if any child matches search
        if (debouncedSearchQuery && transaction.children.some(child => {
          const childDesc = (child.category_name || '').toLowerCase();
          const childParent = (child.parent_category_name || '').toLowerCase();
          return childDesc.includes(lowerQuery) || childParent.includes(lowerQuery);
        })) {
          shouldExpand = true;
        }

        // Check if any child matches category filter
        if (selectedCategoryId) {
          if (selectedCategoryType === 'child' && transaction.children.some(child => child.category_id === selectedCategoryId)) {
            shouldExpand = true;
          } else if (selectedCategoryType === 'parent' && transaction.children.some(child => {
            for (const group of categories) {
              for (const cat of group.children) {
                if (cat.id === child.category_id && group.id === selectedCategoryId) {
                  return true;
                }
              }
            }
            return false;
          })) {
            shouldExpand = true;
          }
        }

        if (shouldExpand) {
          transactionsToExpand.add(transaction.id);
        }
      }
    }

    // Only update if different from current
    setExpandedTransactions(prev => {
      const prevIds = Array.from(prev).sort().join(',');
      const newIds = Array.from(transactionsToExpand).sort().join(',');
      return prevIds === newIds ? prev : transactionsToExpand;
    });
  }, [debouncedSearchQuery, selectedCategoryId, selectedCategoryType, transactions, categories]);

  // Load more transactions when scrolling
  const loadMoreTransactions = useCallback(async () => {
    if (isLoadingMore || !hasMore || loading) return;

    const hasDateFilter = dateFrom && dateTo;
    const hasAccountOrCategoryFilter = selectedAccountId || selectedCategoryId;
    if (!hasDateFilter && !hasAccountOrCategoryFilter) return;

    // Save current scroll position BEFORE loading
    scrollPositionRef.current = window.scrollY;
    console.log(`💾 Saved scroll position: ${scrollPositionRef.current}px`);

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    const nextPage = currentPage + 1;
    const offset = nextPage * PAGE_SIZE;

    console.log(`📄 Loading page ${nextPage}, offset: ${offset}, limit: ${PAGE_SIZE}`);

    try {
      const result = await fetchTransactions(
        selectedPlanMonthId || null,
        selectedCategoryType === 'child' ? selectedCategoryId : undefined,
        selectedCategoryType === 'parent' ? selectedCategoryId : undefined,
        selectedAccountId || undefined,
        dateFrom || undefined,
        dateTo || undefined,
        PAGE_SIZE,
        offset,
        true // append mode
      );

      console.log(`✅ Loaded ${result.transactions.length} transactions, hasMore: ${result.hasMore}`);

      setCurrentPage(nextPage);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Failed to load more transactions:', error);
      scrollPositionRef.current = 0;
      isLoadingMoreRef.current = false;
    } finally {
      setIsLoadingMore(false);
      // NOTE: Don't reset isLoadingMoreRef here - it's reset in the scroll restoration useEffect
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMore, hasMore, loading, currentPage, dateFrom, dateTo, selectedAccountId, selectedCategoryId, selectedPlanMonthId, selectedCategoryType]);

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!loadMoreTriggerRef.current) return;

    // Only set up observer if we have transactions to show and potentially more to load
    if (filteredTransactions.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore && !loading) {
          console.log('🔍 IntersectionObserver triggered - loading more');
          loadMoreTransactions();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observerRef.current.observe(loadMoreTriggerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, loading, loadMoreTransactions, filteredTransactions.length]);

  // Restore scroll position after transactions update (when loading more)
  useEffect(() => {
    if (isLoadingMoreRef.current && scrollPositionRef.current > 0) {
      const savedPosition = scrollPositionRef.current;
      console.log(`🔄 Scroll restoration triggered. Saved: ${savedPosition}px, Current: ${window.scrollY}px`);

      // Use setTimeout with multiple RAF to ensure DOM has fully updated
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            console.log(`📍 Restoring scroll to: ${savedPosition}px (before restore: ${window.scrollY}px)`);
            window.scrollTo({ top: savedPosition, behavior: 'instant' });

            // Verify restoration
            setTimeout(() => {
              console.log(`✅ Scroll after restore: ${window.scrollY}px`);
            }, 50);

            scrollPositionRef.current = 0;
            isLoadingMoreRef.current = false;
          });
        });
      }, 0);
    }
  }, [filteredTransactions.length]); // Trigger when filtered transactions length changes

  // PERFORMANCE: Memoize grouped transactions
  const groupedTransactions = useMemo(() => {
    const groups: Record<string, Transaction[]> = {};
    for (const transaction of filteredTransactions) {
      const date = formatDate(transaction.date);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transaction);
    }
    console.log(`📊 Displaying ${filteredTransactions.length} transactions (from ${transactions.length} total loaded)`);
    return groups;
  }, [filteredTransactions, transactions.length]);

  return (
    <div
      className="min-h-screen bg-[#ffffeb] pb-24 lg:pb-8"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Page Header */}
      <Header
        leftContent={
          <p
            className="text-left text-base font-bold uppercase hover:opacity-80 inline-block"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              transition: 'none',
              fontSize: '14px',
            }}
          >
            {t('history.title')}
          </p>
        }
        maxWidth="max-w-lg"
      />
      {/* Search and Filter Section & Transaction List */}
      <div className="px-6 py-6 max-w-lg lg:max-w-7xl mx-auto lg:flex lg:gap-8 lg:items-start">
        
        {/* Left Sidebar: Filters (Desktop) / Top Section (Mobile) */}
        <div className="lg:w-80 lg:flex-shrink-0 lg:sticky lg:top-24 space-y-4 mb-6 lg:mb-0">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#077f5d]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('history.search_placeholder')}
              className="w-full pl-12 pr-4 py-3.5 bg-white border-2 border-[#077f5d] outline-none font-mono text-sm"
              style={{
                boxShadow: '3px 3px 0 #077f5d',
                transition: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = '3px 3px 0 #077f5d';
              }}
            />
          </div>

          {/* Collapsible Filters (Mobile) / Always Visible (Desktop) */}
          <div className="bg-[#dffbd0] border-2 border-[#077f5d] lg:bg-white"
              style={{ boxShadow: '3px 3px 0 #077f5d' }}
          >
            {/* Filter Header - Collapsible on Mobile */}
            <div className="flex items-center justify-between p-4 lg:p-6 lg:pb-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:pointer-events-none flex items-center gap-2"
              >
                <SlidersHorizontal className="w-4 h-4 text-[#077f5d]" />
                <span
                  className="text-[#077f5d] uppercase"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    lineHeight: '1.4'
                  }}
                >
                  {t('history.filter_short')}
                </span>
                {showFilters ? (
                  <ChevronUp className="w-4 h-4 text-[#077f5d] lg:hidden" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-[#077f5d] lg:hidden" />
                )}
              </button>
              {/* Filter count badge and clear button - only show when filters active */}
              {getActiveFilterCount() > 0 && (
                <div className="flex items-center gap-2">
                  <span
                    className="px-2 py-0.5 bg-[#077f5d] text-white text-xs rounded-full"
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: '10px',
                      fontWeight: 700
                    }}
                  >
                    {getActiveFilterCount()}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearFilters();
                    }}
                    className="px-2 py-1 bg-[#077f5d] text-white border border-[#077f5d] flex items-center gap-1 hover:bg-[#056850]"
                    style={{
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: '7px',
                      boxShadow: '2px 2px 0 #1a1a1a',
                      transition: 'none'
                    }}
                  >
                    <X className="w-3 h-3" />
                    {t('history.clear_filters')}
                  </button>
                </div>
              )}
            </div>

            {/* Filter Content - Collapsible on Mobile, Always Visible on Desktop */}
            <div className={`space-y-3 p-4 pt-2 lg:p-6 lg:pt-4 ${showFilters ? 'block' : 'hidden'} lg:block`}>

            {/* Transaction Type */}
            <div>
                <label
                  className="block text-[#077f5d] mb-1 uppercase"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700
                  }}
                >
                  {t('history.type_label')}
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as 'all' | 'income' | 'expense' | 'transfer')}
                  className="w-full px-3 py-2 text-sm border-2 border-[#077f5d] bg-white font-mono"
                  style={{
                    boxShadow: '2px 2px 0 #077f5d',
                    transition: 'none'
                  }}
                >
                  <option value="all">{t('history.type_all')}</option>
                  <option value="income">{t('history.type_income')}</option>
                  <option value="expense">{t('history.type_expense')}</option>
                  <option value="transfer">{t('history.type_transfer')}</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                <div>
                  <label
                    className="block text-[#077f5d] mb-1 uppercase"
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: '10px',
                      fontWeight: 700
                    }}
                  >
                    {t('history.date_from')}
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 text-sm border-2 border-[#077f5d] bg-white font-mono"
                    style={{
                      boxShadow: '2px 2px 0 #077f5d',
                      transition: 'none'
                    }}
                  />
                </div>
                <div>
                  <label
                    className="block text-[#077f5d] mb-1 uppercase"
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: '10px',
                      fontWeight: 700
                    }}
                  >
                    {t('history.date_to')}
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 text-sm border-2 border-[#077f5d] bg-white font-mono"
                    style={{
                      boxShadow: '2px 2px 0 #077f5d',
                      transition: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Account Filter */}
              <div>
                <label
                  className="block text-[#077f5d] mb-1 uppercase"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700
                  }}
                >
                  {t('history.account_label')}
                </label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border-2 border-[#077f5d] bg-white font-mono"
                  style={{
                    boxShadow: '2px 2px 0 #077f5d',
                    transition: 'none'
                  }}
                >
                  <option value="">{t('history.all_accounts')}</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan Month Filter */}
              <div>
                <label
                  className="block text-[#077f5d] mb-1 uppercase"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700
                  }}
                >
                  {t('history.month_label')}
                </label>
                <select
                  value={selectedPlanMonthId}
                  onChange={(e) => setSelectedPlanMonthId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border-2 border-[#077f5d] bg-white font-mono"
                  style={{
                    boxShadow: '2px 2px 0 #077f5d',
                    transition: 'none'
                  }}
                >
                  <option value="">{t('history.all_months')}</option>
                  {allMonthPlans.map((monthPlan) => {
                    const monthDate = new Date(monthPlan.month);
                    const displayName = formatMonthName(monthDate);
                    return (
                      <option key={monthPlan.id} value={monthPlan.id}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label
                  className="block text-[#077f5d] mb-1 uppercase"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700
                  }}
                >
                  {t('history.category_label')}
                </label>
                <select
                  value={selectedCategoryType && selectedCategoryId ? `${selectedCategoryType}:${selectedCategoryId}` : ''}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border-2 border-[#077f5d] bg-white font-mono"
                  style={{
                    boxShadow: '2px 2px 0 #077f5d',
                    transition: 'none'
                  }}
                >
                  <option value="">{t('history.all_categories')}</option>
                  {categoryOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      className={option.type === 'parent' ? 'font-bold' : 'pl-4'}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content: Transaction List */}
        <div className="flex-1 min-w-0">
        {loading ? (
          <div className="text-center py-12">
            <PixelHourglass size="medium" speed="fast" color="keppel" data-id="budget-status-loader" />
          </div>
        ) : Object.keys(groupedTransactions).length === 0 ? (
          <div className="text-center py-12">
            <div
              className="w-20 h-20 bg-[#a9edba] border-4 border-[#077f5d] flex items-center justify-center mx-auto mb-6"
              style={{ boxShadow: '4px 4px 0 #077f5d' }}
            >
              <Calendar className="w-10 h-10 text-[#077f5d]" />
            </div>
            <p
              className="text-[#077f5d]"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {searchQuery || filterType !== 'all' ? t('history.empty_search') : t('history.empty_default')}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedTransactions).map(([date, transactionList]) => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3">
                  <h3
                    className="text-[#077f5d] uppercase"
                    style={{
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: '10px',
                      lineHeight: '1.4'
                    }}
                  >
                    {date}
                  </h3>
                  <div className="flex-1 h-0.5 bg-[#077f5d]" style={{ opacity: 0.3 }} />
                </div>

                <div className="space-y-2">
                  {transactionList.map((transaction) => {
                    const isExpanded = expandedTransactions.has(transaction.id);
                    const hasChildren = transaction.have_child && transaction.children && transaction.children.length > 0;
                    const isTransfer = !!transaction.transfer_pair_id;
                    const isExpandable = hasChildren || isTransfer;
                    const isSystemTransaction = transaction.category_name === 'Penyesuaian Saldo' || transaction.category_name === 'Balance Adjustment';
                    const isSelected = selectedTransactions.has(transaction.id);
                    const canSelect = selectionMode && !isSystemTransaction;

                    return (
                      <div
                        key={transaction.id}
                        className={`bg-white border-2 ${isSelected ? 'border-[#3cbe8c]' : 'border-[#077f5d]'
                          } overflow-hidden ${isSystemTransaction && selectionMode ? 'opacity-50' : ''}`}
                        style={{
                          boxShadow: isSelected ? '3px 3px 0 #3cbe8c' : '2px 2px 0 #077f5d',
                          transition: 'none'
                        }}
                      >
                        <div className="flex items-stretch">
                          {/* Checkbox on the left - animated slide-in */}
                          {canSelect && (
                            <div
                              className="flex items-center justify-center pl-3 pr-2"
                              onClick={() => toggleTransactionSelection(transaction.id, isSystemTransaction)}
                            >
                              <div
                                className={`flex items-center justify-center w-5 h-5 border-2 cursor-pointer ${isSelected
                                  ? 'bg-[#3cbe8c] border-[#077f5d]'
                                  : 'bg-white border-[#077f5d] hover:bg-[#dffbd0]'
                                  }`}
                                style={{
                                  boxShadow: '1px 1px 0 #077f5d',
                                  transition: 'none'
                                }}
                              >
                                {isSelected && <Check className="w-4 h-4 text-white stroke-[3]" />}
                              </div>
                            </div>
                          )}

                          {/* Transaction content - shrinks when checkbox appears */}
                          <div
                            className={`flex-1 p-4 transition-all duration-300 min-w-0 overflow-hidden ${isExpandable && !selectionMode ? 'cursor-pointer' : ''
                              } ${selectionMode && !isSystemTransaction ? 'cursor-pointer' : ''
                              } ${canSelect ? 'pr-2' : ''
                              }`}
                            onClick={(e) => {
                              // In selection mode, toggle selection (if not system transaction)
                              if (selectionMode && !isSystemTransaction) {
                                toggleTransactionSelection(transaction.id, isSystemTransaction);
                                return;
                              }
                              // Only toggle expand if clicking the card itself, not the action buttons
                              if (isExpandable && !(e.target as HTMLElement).closest('button')) {
                                toggleExpanded(transaction.id);
                              }
                            }}
                          >
                            {getTransactionType(transaction) === 'transfer' ? (
                              // Transfer layout: 3-row format for better date visibility
                              <div className="space-y-1.5">
                                {/* Row 1: Icon + Description + Amount */}
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                                    <ArrowRightLeft className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                    <p className="font-semibold text-gray-900 truncate">
                                      {/* Show category name if transfer is categorized, otherwise show description or 'Transfer' */}
                                      {transaction.category_name
                                        ? (transaction.parent_category_name
                                          ? `${transaction.parent_category_name} - ${translateCategoryName(transaction.category_name)}`
                                          : translateCategoryName(transaction.category_name))
                                        : translateTransactionName(transaction.name, transaction.category_name) || t('history.type_transfer')}
                                    </p>
                                  </div>
                                  <div className="text-lg font-bold text-blue-600 whitespace-nowrap flex-shrink-0 ml-2">
                                    {formatMoney(transaction.amount)}
                                  </div>
                                </div>

                                {/* Row 2: From Account + Date & Time */}
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                                    <span className="text-sm text-gray-500 truncate">
                                      {transaction.from_account_name}
                                    </span>
                                    {isExpanded ? (
                                      <ChevronUp className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                    ) : (
                                      <ChevronDown className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0 ml-2">
                                    {formatTransactionDate(transaction.date)}
                                  </span>
                                </div>

                                {/* Row 3: → To Account + Edit/Delete Icons */}
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
                                    <span className="text-sm text-gray-400 flex-shrink-0">→</span>
                                    <span className="text-sm text-gray-500 truncate">
                                      {transaction.to_account_name}
                                    </span>
                                  </div>
                                  {/* Hide edit/delete for system adjustment transactions and in selection mode */}
                                  {!selectionMode && !isSystemTransaction && (
                                    <div className="flex gap-1 flex-shrink-0 ml-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/transactions/${transaction.id}/edit`);
                                        }}
                                        className="p-1.5 border border-[#077f5d] hover:bg-[#dffbd0]"
                                        style={{ transition: 'none' }}
                                        title="Edit"
                                      >
                                        <Edit2 className="w-4 h-4 text-[#077f5d]" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteConfirmId(transaction.id);
                                        }}
                                        className="p-1.5 border border-red-600 hover:bg-red-50"
                                        style={{ transition: 'none' }}
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              // Regular transaction layout
                              <div className="flex items-start gap-3">
                                {/* Left Column */}
                                <div className="flex-1 min-w-0 space-y-1 overflow-hidden">
                                  <p className="font-semibold text-gray-900 truncate">
                                    {translateTransactionName(transaction.name, transaction.category_name)}
                                  </p>
                                  <div className="flex items-center gap-2 overflow-hidden">
                                    <span className="text-sm text-gray-500 truncate">
                                      {transaction.category_name ? (
                                        transaction.parent_category_name
                                          ? `${transaction.parent_category_name} - ${translateCategoryName(transaction.category_name)}`
                                          : translateCategoryName(transaction.category_name)
                                      ) : (
                                        transaction.have_child
                                          ? t('history.categories_count', { count: transaction.children?.length || 0 })
                                          : (getTransactionType(transaction) === 'income' ? t('history.type_income') : 'Unknown')
                                      )}
                                    </span>
                                    {hasChildren && (
                                      isExpanded ? (
                                        <ChevronUp className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                      ) : (
                                        <ChevronDown className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                      )
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-400 block truncate">{transaction.account_name}</span>
                                </div>

                                {/* Right Column */}
                                <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                                  <div
                                    className={`text-lg font-bold whitespace-nowrap ${getTransactionType(transaction) === 'income'
                                      ? 'text-emerald-600'
                                      : 'text-gray-900'
                                      }`}
                                  >
                                    {getTransactionType(transaction) === 'income' ? '+ ' : '- '}
                                    {formatMoney(transaction.amount)}
                                  </div>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {formatTransactionDate(transaction.date)}
                                  </span>
                                  {/* Hide edit/delete for system adjustment transactions and in selection mode */}
                                  {!selectionMode && !isSystemTransaction && (
                                    <div className="flex gap-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/transactions/${transaction.id}/edit`);
                                        }}
                                        className="p-1.5 border border-[#077f5d] hover:bg-[#dffbd0]"
                                        style={{ transition: 'none' }}
                                        title="Edit"
                                      >
                                        <Edit2 className="w-4 h-4 text-[#077f5d]" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteConfirmId(transaction.id);
                                        }}
                                        className="p-1.5 border border-red-600 hover:bg-red-50"
                                        style={{ transition: 'none' }}
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Children display */}
                        {hasChildren && isExpanded && (
                          <div
                            className="border-t-2 border-[#077f5d] bg-[#dffbd0] px-4 py-3 space-y-2"
                          >
                            {transaction.children!.map((child) => (
                              <div
                                key={child.id}
                                className="flex items-center justify-between py-2 px-3 bg-white border border-[#077f5d]"
                                style={{ boxShadow: '1px 1px 0 #077f5d' }}
                              >
                                <div className="flex-1">
                                  <p
                                    className="text-sm font-bold text-[#077f5d]"
                                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                  >
                                    {child.parent_category_name ? `${child.parent_category_name} - ${child.category_name}` : child.category_name}
                                  </p>
                                </div>
                                <div
                                  className="text-sm font-bold text-[#1a1a1a]"
                                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                >
                                  - {formatMoney(child.amount)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Transfer details display */}
                        {isTransfer && isExpanded && (
                          <div
                            className="border-t-2 border-[#077f5d] bg-[#dffbd0] px-4 py-3 space-y-2"
                          >
                            {/* FROM (expense) */}
                            <div
                              className="flex items-center justify-between py-2 px-3 bg-white border border-[#077f5d]"
                              style={{ boxShadow: '1px 1px 0 #077f5d' }}
                            >
                              <div className="flex-1">
                                <p
                                  className="text-sm font-bold text-[#077f5d]"
                                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                >
                                  {t('history.from_label')}: {transaction.from_account_name}
                                </p>
                              </div>
                              <div
                                className="text-sm font-bold text-red-600"
                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                              >
                                - {formatMoney(transaction.amount)}
                              </div>
                            </div>
                            {/* TO (income) */}
                            <div
                              className="flex items-center justify-between py-2 px-3 bg-white border border-[#077f5d]"
                              style={{ boxShadow: '1px 1px 0 #077f5d' }}
                            >
                              <div className="flex-1">
                                <p
                                  className="text-sm font-bold text-[#077f5d]"
                                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                >
                                  {t('history.to_label')}: {transaction.to_account_name}
                                </p>
                              </div>
                              <div
                                className="text-sm font-bold text-[#3cbe8c]"
                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                              >
                                + {formatMoney(transaction.amount)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Load more trigger */}
            {!loading && filteredTransactions.length > 0 && (
              <div ref={loadMoreTriggerRef} className="py-8">
                {isLoadingMore && (
                  <div className="flex flex-col items-center justify-center gap-3">
                    <Loader2 className="w-8 h-8 text-[#077f5d] animate-spin" />
                    <p
                      className="text-[#077f5d] text-sm"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {t('history.loading_more')}
                    </p>
                  </div>
                )}
                {!hasMore && !isLoadingMore && (
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div
                      className="w-16 h-16 bg-[#dffbd0] border-2 border-[#077f5d] flex items-center justify-center"
                      style={{ boxShadow: '3px 3px 0 #077f5d' }}
                    >
                      <Check className="w-8 h-8 text-[#077f5d]" />
                    </div>
                    <p
                      className="text-[#077f5d] text-sm"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {t('history.all_loaded')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Floating Action Button */}
      {!selectionMode && (
        <button
          onClick={toggleSelectionMode}
          className="fixed bottom-24 right-4 w-12 h-12 bg-[#3cbe8c] text-white border-2 border-[#077f5d] flex items-center justify-center z-40 hover:bg-[#2da877]"
          style={{
            boxShadow: '4px 4px 0 #077f5d',
            transition: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(-2px, -2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(0, 0)';
          }}
          title={t('history.select_transactions')}
        >
          <CheckSquare className="w-6 h-6" />
        </button>
      )}

      {/* Expandable Action Toolbar */}
      {selectionMode && (
        <div className="fixed bottom-24 right-4 z-40">
          <div
            className="flex items-center gap-2 bg-white border-2 border-[#077f5d] px-2 py-2"
            style={{ boxShadow: '4px 4px 0 #077f5d' }}
          >
            {/* Selected count */}
            {selectedTransactions.size > 0 && (
              <div
                className="flex items-center justify-center w-10 h-10 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold text-sm"
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: '11px',
                  boxShadow: '2px 2px 0 #077f5d'
                }}
              >
                {selectedTransactions.size}
              </div>
            )}

            {/* Edit button - navigate to multi-transaction page */}
            {selectedTransactions.size > 0 && (
              <button
                onClick={() => {
                  // Navigate to multi-transaction page with selected IDs
                  const ids = Array.from(selectedTransactions).join(',');
                  navigate(`/transactions/bulk-edit?ids=${ids}`);
                }}
                className="w-10 h-10 flex items-center justify-center text-[#077f5d] border-2 border-[#077f5d] hover:bg-[#dffbd0]"
                style={{
                  boxShadow: '2px 2px 0 #077f5d',
                  transition: 'none'
                }}
                title="Edit"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}

            {/* Delete button */}
            {selectedTransactions.size > 0 && (
              <button
                onClick={() => setShowBulkDeleteConfirm(true)}
                className="w-10 h-10 flex items-center justify-center text-red-600 border-2 border-red-600 hover:bg-red-50"
                style={{
                  boxShadow: '2px 2px 0 #1a1a1a',
                  transition: 'none'
                }}
                title={t('history.delete')}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}

            {/* Close button */}
            <button
              onClick={toggleSelectionMode}
              className="w-10 h-10 flex items-center justify-center bg-[#077f5d] text-white border-2 border-[#077f5d]"
              style={{
                boxShadow: '2px 2px 0 #1a1a1a',
                transition: 'none'
              }}
              title={t('history.cancel')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white border-4 border-[#077f5d] max-w-sm w-full"
            style={{ boxShadow: '4px 4px 0 #077f5d' }}
          >
            {/* Header */}
            <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4">
              <h3
                className="text-white uppercase"
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}
              >
                {t('history.delete_modal_title')}
              </h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="font-mono text-sm text-[#1a1a1a] mb-6">
                {t('history.delete_modal_message')}
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    boxShadow: '4px 4px 0 #077f5d'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(0, 0)';
                  }}
                >
                  {t('history.cancel')}
                </button>
                <button
                  onClick={async () => {
                    try {
                      await deleteTransaction(deleteConfirmId);
                      setDeleteConfirmId(null);
                      // Refresh transactions with HistoryPage's filters - reset to page 0
                      const hasDateFilter = dateFrom && dateTo;
                      const hasCategoryFilter = selectedCategoryId;
                      if (hasDateFilter || hasCategoryFilter || selectedAccountId) {
                        setCurrentPage(0);
                        setHasMore(true);
                        const result = await fetchTransactions(
                          selectedPlanMonthId || null,
                          selectedCategoryType === 'child' ? selectedCategoryId : undefined,
                          selectedCategoryType === 'parent' ? selectedCategoryId : undefined,
                          selectedAccountId || undefined,
                          dateFrom || undefined,
                          dateTo || undefined,
                          PAGE_SIZE,
                          0,
                          false
                        );
                        setHasMore(result.hasMore);
                      }
                      // Refresh accounts and categories to update balances
                      await refreshAccounts();
                      await refreshCategories();
                    } catch (error) {
                      console.error('Failed to delete transaction:', error);
                      alert(t('history.delete_error'));
                    }
                  }}
                  className="flex-[2] px-4 py-3 bg-red-600 text-white border-2 border-red-800 font-bold uppercase"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    boxShadow: '6px 6px 0 #991b1b'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '4px 4px 0 #991b1b';
                    e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '6px 6px 0 #991b1b';
                    e.currentTarget.style.transform = 'translate(0, 0)';
                  }}
                >
                  {t('history.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white border-4 border-[#077f5d] max-w-sm w-full"
            style={{ boxShadow: '4px 4px 0 #077f5d' }}
          >
            {/* Header */}
            <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4">
              <h3
                className="text-white uppercase"
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}
              >
                {t('history.bulk_delete_title', { count: selectedTransactions.size })}
              </h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="font-mono text-sm text-[#1a1a1a] mb-6">
                {t('history.bulk_delete_message', { count: selectedTransactions.size })}
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  className="px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    boxShadow: '4px 4px 0 #077f5d'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(0, 0)';
                  }}
                >
                  {t('history.cancel')}
                </button>
                <button
                  onClick={async () => {
                    setShowBulkDeleteConfirm(false); // Close modal first
                    setNotification({ type: 'loading', message: t('history.bulk_delete_loading') });

                    try {
                      const result = await bulkDeleteTransactions(Array.from(selectedTransactions));
                      setSelectionMode(false);
                      setSelectedTransactions(new Set());

                      // Show success/error message
                      if (result.errors && result.errors.length > 0) {
                        setNotification({
                          type: 'error',
                          message: t('history.bulk_delete_partial', { deleted: result.deleted_count, total: result.total_requested, failed: result.errors.length })
                        });
                      } else {
                        setNotification({
                          type: 'success',
                          message: t('history.bulk_delete_success', { count: result.deleted_count })
                        });
                      }

                      // Refresh data - reset to page 0
                      setCurrentPage(0);
                      setHasMore(true);
                      if (dateFrom && dateTo) {
                        const result = await fetchTransactions(
                          selectedPlanMonthId || null,
                          undefined,
                          undefined,
                          selectedAccountId || undefined,
                          dateFrom,
                          dateTo,
                          PAGE_SIZE,
                          0,
                          false
                        );
                        setHasMore(result.hasMore);
                      }
                      await refreshAccounts();
                      await refreshCategories();
                    } catch (error: any) {
                      console.error('Failed to delete transactions:', error);
                      setNotification({
                        type: 'error',
                        message: error.message || t('history.bulk_delete_error')
                      });
                    }
                  }}
                  className="flex-[2] px-4 py-3 bg-red-600 text-white border-2 border-red-800 font-bold uppercase"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    boxShadow: '6px 6px 0 #991b1b'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '4px 4px 0 #991b1b';
                    e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '6px 6px 0 #991b1b';
                    e.currentTarget.style.transform = 'translate(0, 0)';
                  }}
                >
                  {t('history.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal (Category & Account) */}
      {showBulkCategoryModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white border-4 border-[#077f5d] max-w-sm w-full"
            style={{ boxShadow: '4px 4px 0 #077f5d' }}
          >
            {/* Header */}
            <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4">
              <h3
                className="text-white uppercase"
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}
              >
                {t('history.bulk_edit_title')}
              </h3>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              <p className="font-mono text-sm text-[#1a1a1a]">
                {t('history.bulk_edit_message', { count: selectedTransactions.size })}
              </p>

              {/* Kategori Baru */}
              <div>
                <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
                  {t('history.new_category')}
                </label>
                <select
                  value={bulkCategoryId}
                  onChange={(e) => setBulkCategoryId(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                  style={{ boxShadow: '4px 4px 0 #077f5d' }}
                >
                  <option value="">{t('history.no_change')}</option>
                  {categories.map((group) => (
                    <>
                      <option key={group.id} value="" disabled className="font-bold">
                        {group.name}
                      </option>
                      {group.children.map((child) => (
                        <option key={child.id} value={child.id} className="pl-4">
                          {child.name}
                        </option>
                      ))}
                    </>
                  ))}
                </select>
              </div>

              {/* Sumber Dana Baru */}
              <div>
                <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
                  {t('history.new_account')}
                </label>
                <select
                  value={bulkAccountId}
                  onChange={(e) => setBulkAccountId(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                  style={{ boxShadow: '4px 4px 0 #077f5d' }}
                >
                  <option value="">{t('history.no_change')}</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatMoney(account.balance)}
                    </option>
                  ))}
                </select>
                <p className="mt-2 font-mono text-xs text-gray-600">
                  {t('history.account_change_hint')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowBulkCategoryModal(false);
                    setBulkCategoryId('');
                    setBulkAccountId('');
                  }}
                  className="flex-1 px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    boxShadow: '4px 4px 0 #077f5d'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(0, 0)';
                  }}
                >
                  {t('history.cancel')}
                </button>
                <button
                  onClick={async () => {
                    // Check if at least one field is selected
                    if (!bulkCategoryId && !bulkAccountId) {
                      setNotification({ type: 'error', message: t('history.bulk_edit_select_field') });
                      return;
                    }

                    setShowBulkCategoryModal(false);
                    setNotification({ type: 'loading', message: t('history.bulk_edit_loading') });

                    try {
                      let categoryResult = null;
                      let accountResult = null;
                      let totalErrors = 0;
                      let hasInsufficientBalance = false;

                      // Change category if selected
                      if (bulkCategoryId) {
                        categoryResult = await bulkChangeCategory(Array.from(selectedTransactions), bulkCategoryId);
                        if (categoryResult.errors) totalErrors += categoryResult.errors.length;
                      }

                      // Change account if selected
                      if (bulkAccountId) {
                        accountResult = await bulkChangeAccount(Array.from(selectedTransactions), bulkAccountId);
                        if (accountResult.errors) {
                          totalErrors += accountResult.errors.length;
                          hasInsufficientBalance = accountResult.errors.some((err: any) =>
                            err.error.includes('Insufficient balance') || err.error.includes('insufficient balance')
                          );
                        }
                      }

                      setBulkCategoryId('');
                      setBulkAccountId('');
                      setSelectionMode(false);
                      setSelectedTransactions(new Set());

                      // Show success/error message
                      if (hasInsufficientBalance) {
                        setNotification({
                          type: 'error',
                          message: t('history.bulk_edit_insufficient')
                        });
                      } else if (totalErrors > 0) {
                        setNotification({
                          type: 'error',
                          message: t('history.bulk_edit_partial', { count: totalErrors })
                        });
                      } else {
                        setNotification({
                          type: 'success',
                          message: t('history.bulk_edit_success')
                        });
                      }

                      // Refresh data - reset to page 0
                      setCurrentPage(0);
                      setHasMore(true);
                      if (dateFrom && dateTo) {
                        const result = await fetchTransactions(
                          selectedPlanMonthId || null,
                          undefined,
                          undefined,
                          selectedAccountId || undefined,
                          dateFrom,
                          dateTo,
                          PAGE_SIZE,
                          0,
                          false
                        );
                        setHasMore(result.hasMore);
                      }
                      await refreshAccounts();
                      await refreshCategories();
                    } catch (error: any) {
                      console.error('Failed to change transactions:', error);
                      setNotification({
                        type: 'error',
                        message: error.message || t('history.bulk_edit_error')
                      });
                    }
                  }}
                  disabled={!bulkCategoryId && !bulkAccountId}
                  className="flex-1 px-4 py-3 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold uppercase disabled:opacity-50"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    boxShadow: '6px 6px 0 #077f5d'
                  }}
                  onMouseEnter={(e) => {
                    if (!(!bulkCategoryId && !bulkAccountId)) {
                      e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                      e.currentTarget.style.transform = 'translate(-2px, -2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(0, 0)';
                  }}
                >
                  {t('history.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
          autoClose={notification.type !== 'loading'}
        />
      )}
    </div>
  );
}
